/**
 * API Client with Refresh Token Support
 * 
 * Centralized API client for making authenticated requests to the backend
 * Automatically handles token refresh on 401 errors
 */

import { message } from 'antd';
import type { AuthInfo, TokenInfo } from './types';
import { 
  getTokenInfo, 
  clearTokenInfo, 
  updateTokenInfo 
} from '../utils/storageUtils';
import { 
  broadcastRefresh, 
  broadcastLogout 
} from '../utils/authEventBroadcast';


const API_BASE_URL = import.meta.env.DEV 
? '/api'
: (import.meta.env.VITE_API_BASE_URL || 'https://api.staging.osainternalcontrols.com/api/v1');

/**
 * Track if a refresh is already in progress
 */
let isRefreshingToken = false;

/**
 * Store pending requests that are waiting for token refresh
 */
let pendingRequests: Array<{
  resolve: (value: any) => void;
  reject: (reason: any) => void;
}> = [];

// ========================================
// Internal Token Helpers
// ========================================

/**
 * Get authentication token from storage (internal use)
 */
const getAuthToken = (): string | null => {
  const authInfo = getTokenInfo();
  return authInfo?.accessToken || null;
};

/**
 * Get refresh token from storage (internal use)
 */
const getRefreshToken = (): string | null => {
  const authInfo = getTokenInfo();
  return authInfo?.refreshToken || null;
};

// ========================================
// Logout Handler
// ========================================

/**
 * Handle logout when refresh token fails
 */
const handleLogout = (reason: string = 'refresh_token_expired'): void => {
  // console.log('🚪 Logging out user due to:', reason);
  
  // Show user-friendly error message
  const errorMessages: Record<string, string> = {
    'refresh_token_expired': 'Your session has expired. Please sign in again.',
    'refresh_token_invalid': 'Your session is no longer valid. Please sign in again.',
    'network_error': "We're having trouble reaching the server. Please try again later.",
  };
  
  message.error(errorMessages[reason] || 'Your session has expired. Please sign in again.');
  
  // Clear auth info
  clearTokenInfo();
  
  // Broadcast logout to all tabs
  broadcastLogout(reason);
  
  // Dispatch logout event for Redux to handle
  window.dispatchEvent(new CustomEvent('auth:logout', { 
    detail: { reason } 
  }));
  
  // Redirect to sign in page
  window.location.href = '/auth/signin';
};

// ========================================
// Token Refresh Handler
// ========================================

/**
 * Retry helper with exponential backoff
 * @param fn Function to retry
 * @param maxRetries Maximum number of retries (default: 3)
 * @param delays Array of delays in ms for each retry [200, 500, 1000]
 */
async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  delays: number[] = [200, 500, 1000]
): Promise<T> {
  let lastError: any;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;
      
      // Don't retry on validation errors (invalid token)
      if (error?.code === 'VALIDATION_ERROR' || error?.status === 401) {
        throw error;
      }
      
      // If this is the last attempt, throw
      if (attempt === maxRetries - 1) {
        throw error;
      }
      
      // Wait before retrying
      const delay = delays[attempt] || delays[delays.length - 1];
      // console.log(`⏳ Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
}

/**
 * Refresh the authentication token with retry logic
 */
const handleRefreshToken = async (): Promise<string> => {
  // console.log('🔄 Starting token refresh process');
  
  const currentRefreshToken = getRefreshToken();
  
  if (!currentRefreshToken) {
    // console.log('🚨 No refresh token available, logging out');
    handleLogout('refresh_token_invalid');
    throw new Error('No refresh token available');
  }

  try {
    // console.log('🔄 Attempting to refresh token...');
    
    // Call refresh token API with retry logic
    const result = await retryWithBackoff(async () => {
      const response = await fetch(`${API_BASE_URL}/account/refreshToken`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json',
        },
        body: JSON.stringify({
          refreshToken: currentRefreshToken,
        }),
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw {
          code: data.code || 'UNKNOWN_ERROR',
          message: data.message || 'Failed to refresh token',
          status: response.status,
        };
      }
      
      return data;
    });
    
    if (result.data && result.data.accessToken) {
      const newAuthInfo: AuthInfo = result.data;
      // console.log('✅ Token refresh successful');
   
      // Update auth info in storage
      const newTokenInfo: TokenInfo = {
        accessToken: result.data.accessToken,
        refreshToken: result.data.refreshToken,
      };
      updateTokenInfo(newTokenInfo);
      
      // Broadcast refresh event to all tabs
      broadcastRefresh();
      
      return newAuthInfo.accessToken;
    } else {
      // console.log('🚨 Refresh failed - invalid response structure');
      handleLogout('refresh_token_invalid');
      throw new Error('Invalid refresh token response');
    }
  } catch (error: any) {
    // console.log('🚨 Refresh token request failed:', error);
    
    // Check if it's a validation error (invalid/expired refresh token)
    if (error?.code === 'VALIDATION_ERROR') {
      // console.log('🚨 Invalid or expired refresh token');
      handleLogout('refresh_token_invalid');
    } else {
      // Network or other error
      handleLogout('network_error');
    }
    
    throw error;
  }
};

// ========================================
// Base Fetch Helpers (Internal)
// ========================================

/**
 * Make an unauthenticated fetch request (no token)
 * Reuses common header configuration
 */
const unauthenticatedFetch = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  // Check if body is FormData (for file uploads)
  const isFormData = options.body instanceof FormData;

  const headers: HeadersInit = {
    // Only set Content-Type for JSON requests, let browser set it for FormData
    ...(!isFormData ? { 'Content-Type': 'application/json' } : {}),
    'accept': 'application/json',
    ...options.headers,
  };

  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;

  return fetch(url, {
    ...options,
    headers,
  });
};

/**
 * Make an authenticated fetch request with automatic token refresh
 */
const authenticatedFetch = async (
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> => {
  const token = getAuthToken();

  // Check if body is FormData (for file uploads)
  const isFormData = options.body instanceof FormData;

  const headers: HeadersInit = {
    // Only set Content-Type for JSON requests, let browser set it for FormData
    ...(!isFormData ? { 'Content-Type': 'application/json' } : {}),
    'accept': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...options.headers,
  };
  // console.log('🔄 headers', headers);

  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE_URL}${endpoint}`;

  // Make the initial request
  let response = await fetch(url, {
    ...options,
    headers,
  });

  // Handle 401 Unauthorized - token expired
  if (response.status === 401) {
    // console.log(`🚨 401 Unauthorized for ${endpoint}`);
    
    // Check if this is a sign-in or refresh token request (don't retry these)
    const isAuthEndpoint = endpoint.includes('/sign-in') || 
                          endpoint.includes('/refreshToken') ||
                          endpoint.includes('/password/');
    
    if (isAuthEndpoint) {
      // console.log('🚨 Auth endpoint failed, not retrying');
      return response;
    }

    // If a refresh is already in progress, queue this request
    if (isRefreshingToken) {
      // console.log('🔄 Token refresh in progress, queuing request for:', endpoint);
      
      return new Promise((resolve, reject) => {
        pendingRequests.push({ resolve, reject });
      })
        .then((newToken) => {
          // console.log('🔄 Resolving queued request with new token for:', endpoint);
          // Retry with new token
          const newHeaders: HeadersInit = {
            ...headers,
            'Authorization': `Bearer ${newToken}`,
          };
          return fetch(url, {
            ...options,
            headers: newHeaders,
          });
        })
        .catch((err) => {
          // console.log('🚨 Queued request failed:', err);
          throw err;
        });
    }

    // Start token refresh process
    // console.log('🔄 Starting token refresh for failed request:', endpoint);
    isRefreshingToken = true;

    try {
      const newAccessToken = await handleRefreshToken();
      
      // Resolve all pending requests with the new token
      // console.log(`🔄 Resolving ${pendingRequests.length} pending requests`);
      pendingRequests.forEach(request => {
        request.resolve(newAccessToken);
      });
      
      // Reset pending requests and refresh flag
      pendingRequests = [];
      isRefreshingToken = false;
      // console.log('🔄 Token refresh state reset');
      
      // Retry the original request with the new token
      // console.log('🔄 Retrying original request for:', endpoint);
      const newHeaders: HeadersInit = {
        ...headers,
        'Authorization': `Bearer ${newAccessToken}`,
      };
      
      response = await fetch(url, {
        ...options,
        headers: newHeaders,
      });
      
      return response;
    } catch (error) {
      // console.log('🚨 Token refresh failed, rejecting all pending requests');
      
      // Reject all pending requests
      pendingRequests.forEach(request => {
        request.reject(error);
      });
      
      pendingRequests = [];
      isRefreshingToken = false;
      
      // Return the original 401 response
      return response;
    }
  }

  return response;
};

// ========================================
// Unified API Request Method
// ========================================

/**
 * Unified API request method
 * 
 * @param method HTTP method (GET, POST, PUT, DELETE, PATCH)
 * @param endpoint API endpoint
 * @param data Request body data (for POST, PUT, PATCH) - can be JSON object or FormData
 * @param requiresAuthentication Whether authentication token is required (default: true)
 * 
 * @example
 * // Authenticated request with JSON
 * const user = await apiRequest<User>('GET', '/account/profile');
 * 
 * // Authenticated request with FormData (file upload)
 * const formData = new FormData();
 * formData.append('File', file);
 * const result = await apiRequest<UploadResponse, FormData>('POST', '/file-upload', formData);
 * 
 * // Unauthenticated request
 * const result = await apiRequest<SignInResponse>('POST', '/account/sign-in', data, false);
 */
export const apiRequest = async <T, D = unknown>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
  endpoint: string,
  data?: D,
  requiresAuthentication: boolean = true
): Promise<T> => {
  // Choose fetch method based on authentication requirement
  const fetchFn = requiresAuthentication ? authenticatedFetch : unauthenticatedFetch;
  
  // Check if data is FormData (for file uploads)
  const isFormData = data instanceof FormData;
  
  const response = await fetchFn(endpoint, {
    method,
    // Pass FormData directly, stringify JSON objects
    body: data ? (isFormData ? data : JSON.stringify(data)) : undefined,
  });

  if (!response.ok) {
    const error = await response.json();
    throw error;
  }

  return response.json();
};

export { API_BASE_URL };
